import { useQuery } from "@tanstack/react-query";
import { ParticipantStatusEnum } from "enums/ParticipantStatusEnum";
import { createLogger } from "logging";
import { PatchParticipantModel } from "models/PatchParticipantModel";
import { useApi, useMeeting, useParticipant, usePermissions, useToasts, useTranslations } from "providers";
import React, { PropsWithChildren, useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";

import { EditParticipantPanel } from "./components/EditParticipantPanel";
import { EditParticipantContext, getParticipantFieldValues, ParticipantFieldValues } from "./EditParticipantContext";

const logger = createLogger("EditParticipantContext");

export const EditParticipantProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { handleHttpError } = usePermissions();
  const { reloadMeeting } = useMeeting();
  const { setParticipantUpdating } = useParticipant();
  const { participantsApi } = useApi();
  const i18n = useTranslations();
  const toasts = useToasts();

  const [confirmParticipantUnsaved, setConfirmParticipantUnsaved] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [editParticipantId, setEditParticipantId] = useState<number>(-1);

  const form = useForm<ParticipantFieldValues>({
    defaultValues: undefined,
  });
  const { formState, getValues, reset } = form;

  const queryResult = useQuery({
    queryKey: [`Participant|${editParticipantId}|Edit`],
    queryFn: async () => {
      const data = await participantsApi.getItem(editParticipantId);
      const fieldValues = getParticipantFieldValues(data);
      reset(fieldValues);
      logger.trace("Loaded", data);
      return data;
    },
    enabled: editParticipantId > 0,
    refetchOnWindowFocus: false,
  });
  const { error } = queryResult;
  const isLoading = queryResult.isFetching;

  useEffect(() => {
    handleHttpError(error);
  }, [error, handleHttpError]);

  const { isDirty, isValid } = formState;
  const watchErrors = JSON.stringify(formState.errors, null, 2);
  const isSubmittable = useMemo(() => isValid || watchErrors == "{}", [isValid, watchErrors]);

  const handleClose = useCallback(
    (ignoreDirty?: boolean) => {
      const confirm = isDirty && !ignoreDirty;
      logger.debug(`handleClose(): isDirty:=${isDirty} (ignore:=${ignoreDirty}) -> ${confirm}`);
      if (confirm) {
        setConfirmParticipantUnsaved(true);
      } else {
        setIsSubmitting(false);
        setEditParticipantId(-1);
      }
    },
    [isDirty, setEditParticipantId]
  );

  const handleSuccess = useCallback(
    (title: string) => {
      toasts.updateSuccess(i18n.Participant, title);
      setIsSubmitting(false);
      setEditParticipantId(-1);
      reloadMeeting();
    },
    [i18n, toasts, reloadMeeting]
  );

  const handleError = useCallback(
    (title: string, error: unknown) => {
      toasts.updateFailure(i18n.Participant, title, error);
      throw error;
    },
    [i18n, toasts]
  );

  const getPatch = useCallback(
    (values: ParticipantFieldValues) => {
      const { user, role, status, remarks, canSeeConfidential } = values;

      const patch: PatchParticipantModel = {
        id: editParticipantId,
        user: user?.id,
        role,
        status: ParticipantStatusEnum[status],
        remarks,
        canSeeConfidential,
      };
      return patch;
    },
    [editParticipantId]
  );

  const handleSave = useCallback(async () => {
    logger.debug(`handleSave():`, editParticipantId, isSubmittable);
    if (!editParticipantId) return;
    if (!isSubmittable) return;

    const user = getValues().user;
    if (!user) throw new Error("User is required");
    const userDisplayName = user.displayName || "";

    try {
      setIsSubmitting(true);
      setParticipantUpdating(editParticipantId, true);
      const patch = getPatch(getValues());

      try {
        await participantsApi.update(patch);
        handleSuccess(userDisplayName);
      } catch (error) {
        handleError(userDisplayName, error);
      }
    } catch (error) {
      logger.error("Error saving participant:", error);
    } finally {
      setIsSubmitting(false);
      setParticipantUpdating(editParticipantId, false);
    }
  }, [
    isSubmittable,
    editParticipantId,
    participantsApi,
    handleSuccess,
    handleError,
    getValues,
    getPatch,
    setParticipantUpdating,
  ]);

  const contextValue = useMemo(
    () => ({
      confirmParticipantUnsaved,
      setConfirmParticipantUnsaved,
      editParticipantId,
      setEditParticipantId,
      isLoading,
      isDirty,
      isValid,
      isSubmittable,
      isSubmitting,
      form,
      getPatch,
      handleSave,
      handleClose,
    }),
    [
      confirmParticipantUnsaved,
      editParticipantId,
      isLoading,
      isDirty,
      isValid,
      isSubmittable,
      isSubmitting,
      form,
      getPatch,
      handleSave,
      handleClose,
    ]
  );

  return (
    <EditParticipantContext.Provider value={contextValue}>
      {children}
      <EditParticipantPanel />
    </EditParticipantContext.Provider>
  );
};
